/*
  Loads and renders a Google reCAPTCHA v2 widget (invisible mode).

  Usage:
  ```
    <form>
      <drb-recaptcha-v2 site-key="<%= ENV["RECAPTCHA_SITE_KEY"] %>"></drb-recaptcha-v2>
    </form>
  ```

  Can then be programmatically executed during your form submission logic, for example:
  ```
    // on submit handler of your form

    const recaptchaV2 = form.querySelector('drb-recaptcha-v2');

    if (recaptchaV2 && !recaptchaV2.response) {
      recaptchaV2.execute(yourCallbackFunction); // pass a callback function to handle the response
      return; // prevent form submission until recaptcha is resolved
    }
  ```

  `recaptchaV2.response` will contain the reCAPTCHA response token if there is a
  successful response. This response token will also automatically be inserted into the
  form as a hidden input field by the recaptcha script (with the name `g-recaptcha-response`).
*/

import { LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

@customElement('drb-recaptcha-v2')
class DrbRecaptchaV2 extends LitElement {
  callbackFn: () => void = () => {};

  @property({ attribute: 'site-key', type: String })
  siteKey = '';

  @state()
  widgetId;

  @state()
  response = '';

  connectedCallback() {
    super.connectedCallback();

    if (!this.siteKey) {
      console.warn('drb-recaptcha-v2: Site key is required.');
      return;
    }

    // Load recaptcha script if not already loaded
    if(typeof window.grecaptcha === 'undefined') {
      // Ready script pulled from google docs https://developers.google.com/recaptcha/docs/loading
      window.grecaptcha = {
        ready: function(cb) {
          // window.__grecaptcha_cfg is a global variable that stores reCAPTCHA's
          // configuration. By default, any functions listed in its 'fns' property
          // are automatically executed when reCAPTCHA loads.
          const c = '___grecaptcha_cfg';
          window[c] = window[c] || {};
          (window[c]['fns'] = window[c]['fns'] || []).push(cb);
        }
      };

      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
      script.defer = true;

      document.head.appendChild(script);
    }

    // Wait for the recaptcha script to load, then initialize the widget
    window.grecaptcha.ready(() => {
      this.widgetId = window.grecaptcha.render(this, {
        sitekey: this.siteKey,
        size: 'invisible',

        callback: (response) => {
          this.response = response;
          this.callbackFn?.(response);
          this.dispatchEvent(new CustomEvent('recaptcha-v2:response', { detail: { response } }));
        },

        'expired-callback': () => {
          this.reset();
        },

        'error-callback': () => {
          this.response = '';
          this.dispatchEvent(new CustomEvent('recaptcha-v2:error'));
          console.error('drb-recaptcha-v2: Recaptcha error');
        }
      });
    });
  }

  reset() {
    if (this.widgetId !== undefined) {
      window.grecaptcha.reset(this.widgetId);
      this.response = '';
    }
  }

  execute(callbackFn){
    if (this.widgetId !== undefined) {
      this.callbackFn = callbackFn;
      window.grecaptcha.execute(this.widgetId);
    } else {
      this.dispatchEvent(new CustomEvent('recaptcha-v2:error'));
      console.warn('drb-recaptcha-v2: Widget ID not set. Ensure recaptcha is loaded.');
    }
  }
}

export { DrbRecaptchaV2 };
